import app from 'flarum/forum/app';

/** Caps mirrored from VideoLimits.php, used if the payload lacks the limits. */
const CAP_MB = 200;
const CAP_SECONDS = 59;

/** Whether the current user may post video stories. */
export function canUploadVideo() {
  return !!app.forum.attribute('happydigitalStoriesCanUploadVideo');
}

/** The admin's limits, as the server computed them. */
export function videoLimits() {
  return {
    maxMb: app.forum.attribute('happydigitalStoriesVideoMaxMb') || CAP_MB,
    maxSeconds: app.forum.attribute('happydigitalStoriesVideoMaxSeconds') || CAP_SECONDS,
  };
}

/**
 * Read a video's length from its metadata, without playing or uploading it.
 * Resolves to null when the browser cannot read the file.
 */
function readDuration(file) {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');

    const done = (value) => {
      URL.revokeObjectURL(url);
      video.removeAttribute('src');
      resolve(value);
    };

    video.preload = 'metadata';
    video.muted = true;
    video.onloadedmetadata = () => done(Number.isFinite(video.duration) ? video.duration : null);
    video.onerror = () => done(null);
    video.src = url;
  });
}

/**
 * Check a picked video against the limits.
 *
 * Resolves to { error } where error is a message, or null when the video may
 * be shared. The server repeats these checks — this one only saves someone
 * from waiting on an upload that would be refused.
 */
export function checkVideo(file) {
  const { maxMb, maxSeconds } = videoLimits();
  const refuse = (key) =>
    Promise.resolve({
      error: app.translator.trans('happydigital-stories.forum.' + key, { mb: maxMb, seconds: maxSeconds }),
    });

  if (!/^video\/(mp4|webm|quicktime)$/.test(file.type)) return refuse('video_error_type');
  if (file.size > maxMb * 1024 * 1024) return refuse('video_error_too_large');

  return readDuration(file).then((seconds) => {
    // Unreadable here (e.g. a codec this browser lacks) is not proof of
    // anything; the server's duration check still applies. Rounded, not
    // floored: 59.6 s may come back from mutluresim as 60 and be refused
    // after a full upload, so it is refused here first.
    if (seconds !== null && Math.round(seconds) > maxSeconds) {
      return refuse('video_error_too_long');
    }

    return { error: null };
  });
}
